import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, challengesAPI, interviewsAPI, sessionsAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import '../Dashboard.css';
import './AdminDashboard.css';

const AdminDashboard = ({ onNavigate, currentView }) => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalChallenges: 0,
    totalInterviews: 0,
    activeSessions: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Redirect if not admin
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }

    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [usersRes, challengesRes, interviewsRes] = await Promise.all([
        usersAPI.getAll(),
        challengesAPI.getAll(),
        interviewsAPI.getAll(),
      ]);

      // Calculate stats
      setStats({
        totalUsers: usersRes.data.users?.length || 0,
        totalChallenges: challengesRes.data.challenges?.length || 0,
        totalInterviews: interviewsRes.data.interviews?.length || 0,
        activeSessions: interviewsRes.data.interviews?.filter(i => i.status === 'in_progress').length || 0,
      });

      // Get recent activity (last 5 interviews)
      const recent = interviewsRes.data.interviews
        ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5) || [];

      setRecentActivity(recent);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="admin-dashboard">
        <div className="error-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AdminHeader currentView={currentView} onNavigate={onNavigate} />

      {/* Main Content */}
      <main className="admin-main">
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Stats Overview */}
        <section className="stats-grid">
          <div className="stat-card terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
              </div>
              <span className="terminal-title">system.users</span>
            </div>
            <div className="stat-content">
              <div className="stat-value glow-cyan">{stats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>

          <div className="stat-card terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
              </div>
              <span className="terminal-title">system.challenges</span>
            </div>
            <div className="stat-content">
              <div className="stat-value glow-green">{stats.totalChallenges}</div>
              <div className="stat-label">Total Challenges</div>
            </div>
          </div>

          <div className="stat-card terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
              </div>
              <span className="terminal-title">system.interviews</span>
            </div>
            <div className="stat-content">
              <div className="stat-value text-cyber">{stats.totalInterviews}</div>
              <div className="stat-label">Total Interviews</div>
            </div>
          </div>

          <div className="stat-card terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
              </div>
              <span className="terminal-title">system.sessions</span>
            </div>
            <div className="stat-content">
              <div className="stat-value text-neon">{stats.activeSessions}</div>
              <div className="stat-label">Active Sessions</div>
            </div>
          </div>
        </section>

        {/* Recent Activity */}
        <section className="recent-activity terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">recent.activity</span>
          </div>
          <div className="activity-content">
            {recentActivity.length === 0 ? (
              <div className="empty-state">
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="activity-list">
                {recentActivity.map((interview) => (
                  <div key={interview._id} className="activity-item">
                    <div className="activity-icon">
                      <span className={`status-dot ${interview.status}`}></span>
                    </div>
                    <div className="activity-details">
                      <div className="activity-title">{interview.title}</div>
                      <div className="activity-meta">
                        <span className="text-mono">{interview.candidateEmail}</span>
                        <span className="activity-separator">•</span>
                        <span>{new Date(interview.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="activity-status">
                      <span className={`cyber-badge ${getStatusClass(interview.status)}`}>
                        {interview.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

// Helper function to get status class
const getStatusClass = (status) => {
  switch (status) {
    case 'scheduled':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
    default:
      return '';
  }
};

export default AdminDashboard;
